import React, { Component } from 'react';
import ReactPlayer from 'react-player';
import AgeGateManager from '../utils/ageGate';
import { getSocketApiBase } from '../utils/apiBase';
import './QuadVideoPlayer.css';

class QuadVideoPlayer extends Component {
    constructor(props) {
        super(props);
        this.state = {
            playing: [false, false, false, false], // Play state for each video
            volumes: [0.8, 0.8, 0.8, 0.8],        // Volume for each video
            muted: [false, false, false, false],   // Mute state for each video
            progress: [0, 0, 0, 0],               // Progress for each video (0-1)
            duration: [0, 0, 0, 0],               // Duration for each video
            played: [0, 0, 0, 0]                  // Played time for each video
        };
        this.players = [null, null, null, null];   // Refs to ReactPlayer instances
    }

    handlePlayPause = (index) => {
        const newPlaying = [...this.state.playing];
        newPlaying[index] = !newPlaying[index];
        
        // Allow all videos to play independently - no longer pause others
        this.setState({ playing: newPlaying });
        this.props.onActiveSlotChange?.(index);
    };

    handleVolumeChange = (index, volume) => {
        const newVolumes = [...this.state.volumes];
        newVolumes[index] = parseFloat(volume);
        this.setState({ volumes: newVolumes });
    };

    toggleMute = (index) => {
        const newMuted = [...this.state.muted];
        newMuted[index] = !newMuted[index];
        this.setState({ muted: newMuted });
    };

    setActiveVideo = (index) => {
        this.props.onActiveSlotChange?.(index);
    };

    handlePlayAll = () => {
        const { videos = [] } = this.props;
        const newPlaying = videos.map(video => video !== null);
        this.setState({ playing: newPlaying });
    };

    handlePauseAll = () => {
        const newPlaying = [false, false, false, false];
        this.setState({ playing: newPlaying });
    };

    handleMuteAllExcept = (activeIndex) => {
        const newMuted = [true, true, true, true];
        newMuted[activeIndex] = false;
        this.setState({ muted: newMuted });
    };

    handleUnmuteAll = () => {
        const newMuted = [false, false, false, false];
        this.setState({ muted: newMuted });
    };

    handleProgress = (index, state) => {
        const newProgress = [...this.state.progress];
        const newPlayed = [...this.state.played];
        newProgress[index] = state.played;
        newPlayed[index] = state.playedSeconds;
        this.setState({ 
            progress: newProgress,
            played: newPlayed
        });
    };

    handleDuration = (index, duration) => {
        const newDuration = [...this.state.duration];
        newDuration[index] = duration;
        this.setState({ duration: newDuration });
    };

    handleSeek = (index, played) => {
        if (this.players[index]) {
            this.players[index].seekTo(played, 'fraction');
        }
    };

    formatTime = (seconds) => {
        if (isNaN(seconds)) return '0:00';
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = Math.floor(seconds % 60);
        return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
    };

    handleSlotKeyDown = (event, index) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        this.setActiveVideo(index);
    };

    renderSlotMarker = (index, isActive) => (
        <div className={`quad-slot-marker ${isActive ? 'selected' : ''}`}>
            <span>Screen {index + 1}</span>
            {isActive && <span className="quad-target-label">Target</span>}
        </div>
    );

    renderPlaceholderIcon = () => (
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <rect x="3.5" y="5" width="17" height="13.5" rx="2" stroke="currentColor" strokeWidth="1.5"/>
            <path d="m10 9 5 3-5 3V9Z" fill="currentColor"/>
        </svg>
    );

    renderVideoPlayer = (video, index) => {
        const { playing, volumes, muted, progress, duration, played } = this.state;
        const isActive = this.props.activeSlot === index;
        
        if (!video) {
            return (
                <div
                    className={`quad-video-placeholder ${isActive ? 'active' : ''}`}
                    key={index}
                    onClick={() => this.setActiveVideo(index)}
                    onKeyDown={(event) => this.handleSlotKeyDown(event, index)}
                    role="button"
                    tabIndex={0}
                    aria-pressed={isActive}
                    aria-label={`Screen ${index + 1}, empty${isActive ? ', selected target' : ''}`}
                >
                    {this.renderSlotMarker(index, isActive)}
                    <div className="placeholder-content">
                        <div className="placeholder-icon">{this.renderPlaceholderIcon()}</div>
                        <div className="placeholder-title">
                            {isActive ? `Screen ${index + 1} selected` : `Select Screen ${index + 1}`}
                        </div>
                        <div className="placeholder-subtitle">
                            {isActive
                                ? 'Choose a result to load here.'
                                : 'Select this screen as the next target.'}
                        </div>
                    </div>
                </div>
            );
        }

        if (video.resolving || video.resolveError) {
            return (
                <div
                    className={`quad-video-placeholder ${isActive ? 'active' : ''}`}
                    key={index}
                    onClick={() => this.setActiveVideo(index)}
                    onKeyDown={(event) => this.handleSlotKeyDown(event, index)}
                    role="button"
                    tabIndex={0}
                    aria-pressed={isActive}
                    aria-label={`Screen ${index + 1}${isActive ? ', selected target' : ''}`}
                >
                    {this.renderSlotMarker(index, isActive)}
                    <div
                        className="placeholder-content"
                        role={video.resolveError ? 'alert' : 'status'}
                    >
                        <div className="placeholder-icon">{this.renderPlaceholderIcon()}</div>
                        <div className="placeholder-title">
                            {video.resolving ? 'Preparing video…' : 'Video unavailable'}
                        </div>
                        <div className="placeholder-subtitle">
                            {video.resolving
                                ? 'Resolving a fresh playback stream.'
                                : video.resolveError}
                        </div>
                    </div>
                </div>
            );
        }

        // Check if this is a direct video URL that needs proxying
        const isDirectVideoUrl = video.playable === true || (video.url && (
            video.url.includes('.mp4') || 
            video.url.includes('.m3u8') || 
            video.url.includes('.webm') ||
            video.url.includes('.mov')
        ));
        
        // Check if this is an external URL that needs proxying for CORS
        const isExternalUrl = video.url && (
            video.url.includes('pornhub.com') || 
            video.url.includes('redtube.com') ||
            video.url.includes('phncdn.com') ||
            video.url.includes('ev.phncdn.com') ||
            video.url.includes('xnxx.com') ||
            video.url.includes('xvideos.com')
        );
        
        // Proxy external video URLs to avoid CORS issues
        const backendHost = getSocketApiBase();
        const sourceUrlParam = video.source_url
            ? `&sourceUrl=${encodeURIComponent(video.source_url)}`
            : '';
        const videoUrl = isDirectVideoUrl && isExternalUrl
            ? `${backendHost}/proxy-video?url=${encodeURIComponent(video.url)}&ageGateToken=${encodeURIComponent(AgeGateManager.getAgeGateToken() || '')}${sourceUrlParam}`
            : video.url;

        return (
            <div 
                className={`quad-video-container ${isActive ? 'active' : ''}`} 
                key={index}
                onClick={() => this.setActiveVideo(index)}
                onKeyDown={(event) => this.handleSlotKeyDown(event, index)}
                role="group"
                tabIndex={0}
                aria-label={`Screen ${index + 1}${isActive ? ', selected target' : ''}`}
            >
                <div className="quad-video-player">
                    {this.renderSlotMarker(index, isActive)}
                    <ReactPlayer
                        ref={player => { this.players[index] = player; }}
                        className="react-player-quad"
                        url={videoUrl}
                        width="100%"
                        height="100%"
                        playing={playing[index]}
                        volume={volumes[index]}
                        muted={muted[index]}
                        onError={(error) => {
                            console.error(`🚨 ReactPlayer Error (Video ${index + 1}):`, error);
                            console.log('🎬 Failed URL:', videoUrl);
                        }}
                        onReady={() => {
                            console.log(`✅ ReactPlayer Ready (Video ${index + 1}):`, videoUrl);
                        }}
                        onProgress={(state) => this.handleProgress(index, state)}
                        onDuration={(duration) => this.handleDuration(index, duration)}
                        config={{
                            file: {
                                attributes: {
                                    crossOrigin: 'anonymous'
                                },
                                forceVideo: !videoUrl.includes('.m3u8'),
                                hlsOptions: {
                                    enableWorker: false
                                }
                            }
                        }}
                    />
                    
                    {/* Video Controls Overlay - Always visible, not just on hover */}
                    <div className="quad-video-controls">
                        {/* Progress Bar */}
                        <div className="quad-progress-container">
                            <div 
                                className="quad-progress-bar"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    const rect = e.currentTarget.getBoundingClientRect();
                                    const clickX = e.clientX - rect.left;
                                    const newProgress = clickX / rect.width;
                                    this.handleSeek(index, newProgress);
                                }}
                            >
                                <div 
                                    className="quad-progress-played"
                                    style={{ width: `${(progress[index] || 0) * 100}%` }}
                                />
                            </div>
                        </div>
                        
                        <div className="quad-control-row">
                            <button 
                                className="quad-control-btn play-pause-btn" 
                                onClick={(e) => {
                                    e.stopPropagation();
                                    this.handlePlayPause(index);
                                }}
                                title={playing[index] ? "Pause" : "Play"}
                            >
                                {playing[index] ? (
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                                        <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z"/>
                                    </svg>
                                ) : (
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                                        <path d="M8 5v14l11-7z"/>
                                    </svg>
                                )}
                            </button>
                            
                            <button 
                                className="quad-control-btn volume-btn" 
                                onClick={(e) => {
                                    e.stopPropagation();
                                    this.toggleMute(index);
                                }}
                                title={muted[index] ? "Unmute" : "Mute"}
                            >
                                {muted[index] || volumes[index] === 0 ? (
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                                        <path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z"/>
                                    </svg>
                                ) : (
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                                        <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/>
                                    </svg>
                                )}
                            </button>
                            
                            <input
                                type="range"
                                min={0}
                                max={1}
                                step={0.1}
                                value={muted[index] ? 0 : volumes[index]}
                                onChange={(e) => {
                                    e.stopPropagation();
                                    this.handleVolumeChange(index, e.target.value);
                                }}
                                className="quad-volume-slider"
                                title="Volume"
                            />
                            
                            <button 
                                className="quad-control-btn fullscreen-btn" 
                                onClick={(e) => {
                                    e.stopPropagation();
                                    // Try to make the video fullscreen
                                    if (this.players[index] && this.players[index].getInternalPlayer()) {
                                        const videoElement = this.players[index].getInternalPlayer();
                                        if (videoElement.requestFullscreen) {
                                            videoElement.requestFullscreen();
                                        } else if (videoElement.webkitRequestFullscreen) {
                                            videoElement.webkitRequestFullscreen();
                                        } else if (videoElement.mozRequestFullScreen) {
                                            videoElement.mozRequestFullScreen();
                                        } else if (videoElement.msRequestFullscreen) {
                                            videoElement.msRequestFullscreen();
                                        }
                                    }
                                }}
                                title="Fullscreen"
                            >
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z"/>
                                </svg>
                            </button>
                            
                            <div className="quad-time-display">
                                {this.formatTime(played[index])} / {this.formatTime(duration[index])}
                            </div>
                        </div>
                    </div>

                    {/* Video Info */}
                    <div className="quad-video-info">
                        <div className="quad-video-title">{video.title}</div>
                        <div className="quad-video-meta">
                            <span className="quad-video-uploader">{video.uploader || 'Unknown'}</span>
                            {video.view_count && (
                                <span className="quad-video-views">{video.view_count.toLocaleString()} views</span>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    render() {
        const { videos = [] } = this.props;
        
        // Ensure we have exactly 4 video slots
        const videoSlots = Array(4).fill(null).map((_, index) => 
            videos[index] || null
        );

        return (
            <div className="quad-video-player-container">
                <div className="quad-control-bar">
                    <div className="quad-global-controls">
                        <button 
                            className="quad-global-btn play-all-btn"
                            onClick={this.handlePlayAll}
                            title="Play All Videos"
                        >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M8 5v14l11-7z"/>
                            </svg>
                            Play All
                        </button>
                        
                        <button 
                            className="quad-global-btn pause-all-btn"
                            onClick={this.handlePauseAll}
                            title="Pause All Videos"
                        >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z"/>
                            </svg>
                            Pause All
                        </button>
                        
                        <button 
                            className="quad-global-btn mute-others-btn"
                            onClick={() => this.handleMuteAllExcept(this.props.activeSlot)}
                            title="Mute All Except Selected Screen"
                        >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z"/>
                            </svg>
                            Solo Selected
                        </button>
                        
                        <button 
                            className="quad-global-btn unmute-all-btn"
                            onClick={this.handleUnmuteAll}
                            title="Unmute All Videos"
                        >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/>
                            </svg>
                            Unmute All
                        </button>
                    </div>
                    
                    <div className="quad-status" aria-live="polite">
                        <span className="quad-target-summary">
                            Target: Screen {this.props.activeSlot + 1}
                        </span>
                        <span>{videoSlots.filter(v => v !== null).length} of 4 videos loaded</span>
                    </div>
                </div>
                
                <div className="quad-video-grid">
                    {videoSlots.map((video, index) => this.renderVideoPlayer(video, index))}
                </div>
            </div>
        );
    }
}

export default QuadVideoPlayer;